import { lazy, Suspense, useContext, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';
import BubbleLoader from 'App/Components/Loaders/BubbleLoader';
import {
    setMultipleStorageValues,
    getDefaults,
    getStorageValue,
    setStorageValue,
} from 'App/Helpers';
import { useAnalytics } from 'App/Services/Analytics';
import { PushContext } from 'App/Services/Notifications';
import { PwaContext } from 'App/Services/Pwa';

import { gaPresetValues, STORAGE_KEY } from './configs';
import Landing from './pages/Landing';
import { AppContext } from './providers';

import './main.scss';

const DynamicPhaseRenderer = lazy(
    () => import('./components/DynamicPhaseRenderer')
);

const App = () => {
    const { showPushPhase, setPushPromptTotal } = useContext(PushContext);
    const { showPwaPhase, setPwaPhaseTotal } = useContext(PwaContext);
    const {
        phase,
        checkInitialPhase,
        updatePhase,
        setOffer,
        offerConfigReady,
        sharpstarService,
        appConfig: { domain, buildVersion, buildIteration, activeLanguages },
        urlParams: { rid, refid1, refid2, refid3 },
        botInfo: { isBot, customIsBot, failedChecks, visitorId },
    } = useContext(AppContext);
    const { sendGameDesignEvent, sendGoogleBasicEvent } = useAnalytics();

    const [ageConfirmationStatus, setAgeConfirmationStatus] = useState(() =>
        getStorageValue(STORAGE_KEY, 'app.ageConfirmation')
    );
    const hasFetchedInitialOfferRef = useRef(false);

    useEffect(() => {
        checkInitialPhase(showPushPhase, showPwaPhase);
    }, []);

    useEffect(() => {
        if (phase === 'pending' && offerConfigReady) {
            updatePhase(showPushPhase ? 'push' : showPwaPhase ? 'pwa' : 'game');
        }
    }, [phase, offerConfigReady, showPushPhase, showPwaPhase, updatePhase]);

    const handleAgeMisclicked = () => {
        setStorageValue(STORAGE_KEY, 'app.ageConfirmation', 'above18');
        setAgeConfirmationStatus('above18');
        updatePhase(showPushPhase ? 'push' : showPwaPhase ? 'pwa' : 'game');

        sendGameDesignEvent('HP:AgeVerificationModal:Misclicked');
        sendGoogleBasicEvent('hp', 'age_verification_modal_misclicked');
    };

    useEffect(() => {
        if (hasFetchedInitialOfferRef.current) return;
        if (ageConfirmationStatus === 'below18') return;
        if (!sharpstarService || typeof isBot !== 'boolean') return;
        hasFetchedInitialOfferRef.current = true;

        const fetchOffers = async () => {
            try {
                const response = await sharpstarService.getOffers({
                    isLead: 0,
                    hash: undefined,
                    botFailedChecks: failedChecks,
                    customIsBot,
                    visitorId,
                    isBot,
                    rid,
                    refid1,
                    refid2,
                    refid3,
                });

                const { id, content } = response.offers[0];

                setOffer({ id, content });
                setPushPromptTotal(Number(content['push-prompt-total'] || 0));
                setPwaPhaseTotal(Number(content['pwa-prompt-total'] || 0));

                setMultipleStorageValues(STORAGE_KEY, {
                    'app.offerId': id,
                    'game.build': content['build-version'],
                });
            } catch (e) {
                Sentry.captureException(e);
                setPushPromptTotal(0);
                setPwaPhaseTotal(0);

                const remoteConfig = await getDefaults({
                    gaPreset: gaPresetValues,
                    activeLanguages,
                    buildIteration,
                    buildVersion,
                    domain,
                });

                setOffer({ id: 0, content: remoteConfig });
            }
        };

        void fetchOffers();
    }, [ageConfirmationStatus, sharpstarService, isBot]);

    const loader = (
        <BubbleLoader loaderColor={'#09328d'} backgroundColor={'#b7d6fe'} />
    );

    return (
        <>
            {phase === 'landing' ? (
                <Landing
                    ageConfirmationStatus={ageConfirmationStatus}
                    onMisclicked={handleAgeMisclicked}
                    showPwaPhase={showPwaPhase}
                    showPushPhase={showPushPhase}
                />
            ) : phase === 'pending' ? (
                loader
            ) : (
                <Suspense fallback={loader}>
                    {offerConfigReady && <DynamicPhaseRenderer />}
                </Suspense>
            )}
        </>
    );
};

export default App;
